/**
 * Generic icon preloader utilities
 * Works with any icon registry that uses createLazyIcon
 *
 * @module createIconPreloader
 */
import {getSafely} from 'shared/web';

import type {LazyIconEntry} from './createLazyIcon';

export type IconRegistry<TIconKey extends string = string> = {
  [key in TIconKey]: LazyIconEntry | unknown;
};

export interface IconPreloader<TIconKey extends string> {
  /**
   * Preloads a single icon to prevent loading delays
   * Safe to call multiple times - uses internal memoization
   */
  preloadIcon: (key: TIconKey) => void;

  /**
   * Preloads multiple icons in parallel
   * Useful for preloading all icons needed for a page/route
   */
  preloadIcons: (keys: readonly TIconKey[]) => void;

  /**
   * Auto-preload commonly used icons during browser idle time
   * Uses requestIdleCallback to avoid blocking main thread
   */
  autoPreloadIcons: (icons?: readonly TIconKey[]) => void;
}

/**
 * Creates a preloader for an icon registry
 * Provides type-safe methods for preloading icons
 *
 * @param lazyIconRegistry - Registry of lazy icons created with createLazyIcon
 * @param defaultCommonIcons - Optional default set of icons to auto-preload
 * @returns Object with preload utility functions
 *
 * @example
 * ```typescript
 * const lazyIcons = {
 *   'app/chart': createLazyIcon(() => import('./chart.svg')),
 *   'app/graph': createLazyIcon(() => import('./graph.svg')),
 * } as const;
 *
 * type LazyIconKey = keyof typeof lazyIcons;
 *
 * export const {
 *   preloadIcon,
 *   preloadIcons,
 *   autoPreloadIcons
 * } = createIconPreloader<LazyIconKey>(lazyIcons, ['app/chart']);
 * ```
 */
export const createIconPreloader = <TIconKey extends string>(
  lazyIconRegistry: Record<TIconKey, LazyIconEntry>,
  defaultCommonIcons?: readonly TIconKey[]
): IconPreloader<TIconKey> => {
  const preloadIcon = (key: TIconKey): void => {
    const iconEntry = getSafely(lazyIconRegistry, key);
    if (iconEntry?.preload) {
      iconEntry.preload().catch((error) => {
        console.error(`Failed to preload icon: ${String(key)}`, error);
      });
    }
  };

  const preloadIcons = (keys: readonly TIconKey[]): void => {
    keys.forEach((key) => preloadIcon(key));
  };

  const autoPreloadIcons = (icons?: readonly TIconKey[]): void => {
    const iconsToPreload = icons ?? defaultCommonIcons ?? [];

    if (iconsToPreload.length === 0) {
      return;
    }

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(
        () => {
          preloadIcons(iconsToPreload);
        },
        {timeout: 2000}
      );
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => preloadIcons(iconsToPreload), 2000);
    }
  };

  return {
    preloadIcon,
    preloadIcons,
    autoPreloadIcons,
  };
};
